import type { PaginationConfig } from '../types'

/**
 * `resolvePageSize` / `buildPageStarts` 是纯函数实现，
 * 直接从 React 端 `pagination-utils.ts` 平移过来，逻辑 1:1 对齐。
 *
 * 之所以放在 composables 目录、但不用 ref/computed 包装：
 * - 输入均可为响应式 unref 后的普通值
 * - 各容器组件按需在 computed 内组合调用，保持响应式粒度可控
 */

export interface ResolvePageSizeOptions {
  /** 分页配置。为空时返回 dataLength，表示全量展示不分页。 */
  pagination?: PaginationConfig
  /** 数据总条数。 */
  dataLength: number
  /** 容器实际宽度。auto 模式下按此换算每页可容纳数量。 */
  containerWidth: number
  /** 单张卡片最小宽度。 */
  minItemWidth: number
  /** 卡片间距。 */
  gap: number
  /** 若上层直接指定 columns，则以 columns 为准。 */
  columns?: number
  /** 每张卡片之外的额外宽度（如公式模式下预留的运算符宽度）。 */
  extraPerItem?: number
}

/**
 * 计算实际生效的 pageSize。
 *
 * 优先级：
 * 1. 未传 `pagination` → 返回 `dataLength`（不分页）
 * 2. `pagination.pageSize` 显式为数字 → clamp 到 `[1, dataLength]`
 * 3. `pagination.pageSize === 'auto'`：
 *    - 有 `columns` → 直接取 `columns`（clamp）
 *    - 否则按容器宽度 / (minItemWidth + gap + extraPerItem) 向下取整
 */
export function resolvePageSize(options: ResolvePageSizeOptions): number {
  const {
    pagination,
    dataLength,
    containerWidth,
    minItemWidth,
    gap,
    columns,
    extraPerItem = 0,
  } = options

  if (!pagination) {
    return dataLength
  }

  const requestedPageSize = pagination.pageSize ?? 'auto'

  if (requestedPageSize !== 'auto') {
    return Math.min(Math.max(requestedPageSize, 1), dataLength)
  }

  if (dataLength <= 0) {
    return 0
  }

  if (columns) {
    return Math.min(Math.max(columns, 1), dataLength)
  }

  const widthUnit = minItemWidth + gap + extraPerItem
  const availableWidth = containerWidth > 0 ? containerWidth : widthUnit
  const autoPageSize = Math.floor((availableWidth + gap) / widthUnit)

  return Math.min(Math.max(autoPageSize, 1), dataLength)
}

/**
 * 根据总条数、每页数量、步长生成"每页起始索引"数组。
 *
 * - `step === 'auto'`：每次翻整页（相当于 step === pageSize），
 *   但最后一页会对齐到 `total - pageSize`，避免尾页空档
 * - `step` 为数字：按固定步长滑动，直到剩余不足 step 时停止
 */
export function buildPageStarts(total: number, pageSize: number, step: number | 'auto'): number[] {
  if (total <= 0 || pageSize <= 0) {
    return [0]
  }

  const starts = [0]
  let current = 0

  if (step === 'auto') {
    const maxStart = Math.max(total - pageSize, 0)
    while (current < maxStart) {
      const next = Math.min(current + pageSize, maxStart)
      if (next === current) {
        break
      }
      starts.push(next)
      current = next
    }
    return starts
  }

  // step 为非正数(负数/0)时会导致 `current + step < total` 恒成立而死循环，
  // 按整页翻页(pageSize)兜底；再用 Math.max(..., 1) 兜底 pageSize 万一也非正的情况，
  // 确保步长恒 >= 1，while 每轮必然递增、循环有界，杜绝 CPU/内存被打满。
  const safeStep = Math.max(step > 0 ? step : pageSize, 1)

  while (current + safeStep < total) {
    const next = current + safeStep
    if (next === current) {
      break
    }
    starts.push(next)
    current = next
  }

  return starts
}